import keyBy from 'lodash/keyBy'
import mapValues from 'lodash/mapValues'
import merge from 'lodash/merge'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'
import objects from '@/objects'

export default {
  config(state) {
    return {...state.config, ...state.sessionConfig, ...state.userConfig}
  },
  // account
  account(state, getters) {
    return objects.Account.create(getters.accounts.find(account => account.id === state.account.id))
  },
  member(state, getters) {
    return objects.Member.create(getters.members.find(member => member.id === getters.account.member.id))
  },
  role(state, getters) {
    return getters.rolesById[getters.member.role] || objects.Role.create(getters.member.isOwner ? {name: 'Owner'} : {name: 'Empty'})
  },
  user(state) {
    return objects.User.create(state.user)
  },
  tariff(state, getters) {
    return objects.Tariff.create(getters.tariffs.find(tariff => tariff.id === getters.account.tariff))
  },
  access(state, {actionsById, config, hosts, member, projects}) {
    const memberActions = config.owner ? mapValues(actionsById[member.id], () => 1) : actionsById[member.id] || {}
    const hasUsableProjects = projects.some(project => actionsById[project.id].projectUsage)
    const hasUsableHosts = hosts.some(host => actionsById[host.id].hostUsage)
    const canCreateUsableProjects = memberActions.projectCreate && memberActions.projectUsage
    const canCreateUsableHosts = memberActions.hostCreate && memberActions.hostUsage
    memberActions.serviceCreate = (hasUsableProjects || canCreateUsableProjects) && (hasUsableHosts || canCreateUsableHosts) || config.owner
    return memberActions
  },
  actionsById(state, getters) {
    const actionsById = {}
    const member = getters.member
    const role = getters.role
    const entities = getters.members.concat(getters.hosts, getters.projects, getters.services)
    const actionsByTarget = {
      member: ['accManagement', 'hostCreate', 'hostDestroy', 'hostInfo', 'hostTerminal', 'hostUpdate', 'hostUsage', 'keyCreate', 'keyDestroy', 'projectCreate', 'projectDestroy', 'projectExport', 'projectInfo', 'projectUpdate', 'projectUsage', 'providerCreate', 'providerDestroy', 'providerUpdate', 'serviceChangeProject', 'serviceDestroy', 'serviceInfo', 'serviceMoveContainer', 'serviceScale', 'serviceTerminal', 'serviceUpdate', 'tariffManagement'],
      project: ['projectInfo', 'projectUpdate', 'projectUsage', 'projectDestroy', 'serviceInfo', 'serviceUpdate', 'serviceTerminal', 'serviceMoveContainer', 'serviceScale', 'serviceChangeProject', 'serviceDestroy'],
      service: ['serviceInfo', 'serviceUpdate', 'serviceTerminal', 'serviceScale', 'serviceMoveContainer', 'serviceChangeProject', 'serviceDestroy'],
      host: ['hostInfo', 'hostUpdate', 'hostUsage', 'hostTerminal', 'hostDestroy'],
    }
    let hasUsableProjects = false
    let hasUsableHosts = false

    for (const entity of entities) {
      actionsById[entity.id] = {}
      for (const action of actionsByTarget[entity.target]) {
        if (entity.target === 'member') {
          actionsById[entity.id][action] = entity.isOwner ? 1 : merge(
            {result: entity.role ? entity._role.actions[action] : undefined},
            {result: entity.actions[action]},
          ).result
        } else {
          actionsById[entity.id][action] = (member.isOwner || getters.config.owner) ? 1 : merge(
            {result: (entity.rules.global[role.id] || {actions: {}}).actions[action]},
            {result: (entity.rules.global[member.id] || {actions: {}}).actions[action]},
            {result: (entity.rules.project && entity.rules.project[role.id] || {actions: {}}).actions[action]},
            {result: (entity.rules.project && entity.rules.project[member.id] || {actions: {}}).actions[action]},
            {result: (entity.rules.local[role.id] || {actions: {}}).actions[action]},
            {result: (entity.rules.local[member.id] || {actions: {}}).actions[action]}
          ).result
        }
      }
      if (entity.target === 'host' && actionsById[entity.id].hostInfo && actionsById[entity.id].hostUsage) {
        hasUsableHosts = true
      }
      if (entity.target === 'project' && actionsById[entity.id].projectInfo && actionsById[entity.id].projectUsage) {
        hasUsableProjects = true
      }
    }
    const canCreateUsableHosts = actionsById[member.id] && actionsById[member.id].hostCreate && actionsById[member.id].hostUsage
    const canCreateUsableProjects = actionsById[member.id] && actionsById[member.id].projectCreate && actionsById[member.id].projectUsage

    for (const project of getters.projects) {
      actionsById[project.id].serviceCreate = (hasUsableProjects || canCreateUsableProjects) && (hasUsableHosts || canCreateUsableHosts)
    }

    // add owner members
    for (const account of getters.accounts) {
      if (account.member && !actionsById[account.member.id]) {
        actionsById[account.member.id] = {}
        for (const action of actionsByTarget.member) {
          actionsById[account.member.id][action] = account.member.isOwner || getters.config.owner
        }
      }
    }
    return actionsById
  },
  // dict
  instances(state) {
    return Object.values(state.instancesById)
  },
  instancesById(state) {
    return state.instancesById
  },
  regions(state) {
    return Object.values(state.regionsById)
  },
  regionsById(state) {
    return state.regionsById
  },
  tariffs(state) {
    return Object.values(state.tariffsById)
  },
  tariffsById(state) {
    return state.tariffsById
  },
  tariffsByName(state, getters) {
    return keyBy(getters.tariffs, 'name')
  },
  // entities
  entities(state, getters) {
    const targets = Object.values(objects).filter(object => object.objectType == 'entityType').map(object => object.typeName)
    return targets.reduce((array, target) => array.concat(getters[`${target}s`]), [])
  },
  accounts(state) {
    return Object.values(state.accountsById)
  },
  accountsById(state) {
    return state.accountsById
  },
  accountsByName(state, getters) {
    return keyBy(getters.accounts, 'name')
  },
  containers(state) {
    return Object.values(state.containersById)
  },
  containersByHost(state, {hosts, containers}) {
    const containersByHost = {}
    for (const index in hosts) {
      const hostId = hosts[index].id
      containersByHost[hostId] = containers.filter(container => container.host === hostId)
    }
    return containersByHost
  },
  containersById(state) {
    return state.containersById
  },
  containersByIP(state, {hosts, containersByHost}) {
    const containersByIP = {}
    for (const index in hosts) {
      const {id, ipAddress} = hosts[index]
      if (ipAddress) {
        containersByIP[ipAddress] = containersByHost[id]
      }
    }
    return containersByIP
  },
  containersByService(state, {services, containers}) {
    const containersByService = {}
    for (const index in services) {
      const serviceId = services[index].id
      containersByService[serviceId] = containers.filter(container => container.service === serviceId)
    }
    return containersByService
  },
  containersByProject(state, {projects, containers, servicesById}) {
    const containersByProject = {}
    for (const index in projects) {
      const projectId = projects[index].id
      containersByProject[projectId] = containers.filter(container => servicesById[container.service] && servicesById[container.service].project === projectId)
    }
    return containersByProject
  },
  hosts(state) {
    return Object.values(state.hostsById)
  },
  hostsById(state) {
    return state.hostsById
  },
  hostsByIP(state, {hosts}) {
    return keyBy(hosts.filter(host => host.ipAddress), 'ipAddress')
  },
  hostsByName(state, getters) {
    return keyBy(getters.hosts, 'name')
  },
  hostsByKey(state, {keys, hosts}) {
    const hostsByKeys = {}
    for (const index in keys) {
      const keyId = keys[index].id
      hostsByKeys[keyId] = hosts.filter(host => host.keys.includes(keyId))
    }
    return hostsByKeys
  },
  hostsByProject(state, {projects, hostsById, containersByProject}) {
    const hostsByProject = {}
    for (const index in projects) {
      const projectId = projects[index].id
      hostsByProject[projectId] = uniqBy(containersByProject[projectId].map(container => hostsById[container.host]).filter(host => host), 'id')
    }
    return hostsByProject
  },
  hostsByProvider(state, {providers, hosts}) {
    const hostsByProvider = {}
    for (const index in providers) {
      const providerId = providers[index].id
      hostsByProvider[providerId] = hosts.filter(host => host.provider === providerId)
    }
    return hostsByProvider
  },
  hostsByService(state, {services, hostsById, containersByService}) {
    const hostsByService = {}
    for (const index in services) {
      const serviceId = services[index].id
      hostsByService[serviceId] = containersByService[serviceId].map(container => hostsById[container.host]).filter(host => host)
    }
    return hostsByService
  },
  hostsByType(state, {hosts}) {
    const hostsByType = {}
    for (const type in objects._hostsByType) {
      hostsByType[type] = hosts.filter(host => host.providerType === type)
    }
    return hostsByType
  },
  hostLabels(state, getters) {
    return uniq(getters.hosts.reduce((items, item) => items.concat(item.labels), []))
  },
  keys(state) {
    return Object.values(state.keysById)
  },
  keysById(state) {
    return state.keysById
  },
  keysByName(state, getters) {
    return keyBy(getters.keys, 'name')
  },
  keysByType(state, {keys}) {
    const keysByType = {}
    for (const type in objects._keysByType) {
      keysByType[type] = keys.filter(key => key.type === type)
    }
    return keysByType
  },
  members(state) {
    return Object.values(state.membersById)
  },
  membersById(state) {
    return state.membersById
  },
  membersByRole(state, {members, roles}) {
    const membersByRole = {}
    for (const role of roles.concat({id: null})) {
      membersByRole[role.id] = members.filter(member => member.role === role.id)
    }
    return membersByRole
  },
  projects(state) {
    return Object.values(state.projectsById)
  },
  projectsByHost(state, {hosts, servicesByHost, projectsById}) {
    const projectsByHost = {}
    for (const index in hosts) {
      const hostId = hosts[index].id
      projectsByHost[hostId] = servicesByHost[hostId].map(service => projectsById[service.project]).filter(project => project)
    }
    return projectsByHost
  },
  projectsById(state) {
    return state.projectsById
  },
  projectsByName(state, getters) {
    return keyBy(getters.projects, 'name')
  },
  providers(state) {
    return Object.values(state.providersById)
  },
  providersById(state) {
    return state.providersById
  },
  providersByName(state, getters) {
    return keyBy(getters.providers, 'name')
  },
  providersByPurpose(state, {providers}) {
    const purposes = ['hosting', 'storage', 'logs']
    const providersByPurpose = {}
    for (const index in purposes) {
      const purpose = purposes[index]
      providersByPurpose[purpose] = providers.filter(provider => provider.purpose == purpose)
    }
    return providersByPurpose
  },
  providersByType(state, {providers}) {
    const providersByType = {}
    for (const type in objects._providersByType) {
      providersByType[type] = providers.filter(provider => provider.type === type)
    }
    return providersByType
  },
  roles(state) {
    return Object.values(state.rolesById)
  },
  rolesById(state) {
    return state.rolesById
  },
  services(state) {
    return Object.values(state.servicesById)
  },
  servicesByHost(state, {hosts, servicesById, containersByHost}) {
    const servicesByHost = {}
    for (const index in hosts) {
      const hostId = hosts[index].id
      servicesByHost[hostId] = containersByHost[hostId].map(container => servicesById[container.service]).filter(service => service)
    }
    return servicesByHost
  },
  servicesById(state) {
    return state.servicesById
  },
  servicesByName(state, getters) {
    return keyBy(getters.services, 'name')
  },
  servicesByProject(state, {projects, services}) {
    const servicesByProject = {}
    for (const index in projects) {
      const projectId = projects[index].id
      servicesByProject[projectId] = services.filter(service => service.project == projectId)
    }
    return servicesByProject
  },
  servicesByProvider(state, {providers, services}) {
    const servicesByProvider = {}
    for (const index in providers) {
      const providerId = providers[index].id
      servicesByProvider[providerId] = services.filter(service => service.logProvider === providerId)
    }
    return servicesByProvider
  },
  servicesByType(state, {services}) {
    const servicesByType = {}
    for (const type in objects._servicesByType) {
      servicesByType[type] = services.filter(service => service.type === type)
    }
    return servicesByType
  },
  users(state, getters) {
    return [getters.user]
  },
}
